import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Skeleton } from "@shared-ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EmptyState } from "@/components/ui/empty-state";
import { DataTable } from "@/features/catalog/components/data-table";
import {
	SourceDialog,
	type SourceDialogMode,
} from "@/features/catalog/components/source-dialog";
import {
	type ImportSource,
	listDueImportSources,
	listImportSources,
} from "@/features/catalog/services/import-api.service";
import { EXTRACTOR_OPTIONS } from "../lib/extractor-options.js";

/**
 * The sources registry: what the catalogue imports from, and how to add to it.
 *
 * This screen exists because the import pipeline shipped without it. `GET
 * /sources` had a route and a picker on the imports dashboard; registering one
 * meant an INSERT by hand, which made a merged feature unreachable to the people
 * it was built for.
 *
 * A LIST WITH A PER-ROW EDIT, not a create form. The thing an operator needs
 * most often after registering a source is to correct the cadence or the
 * extractor they picked wrong -- and a create-only screen leaves `PATCH
 * /sources/:id` with no operator path, which is the same gap one layer up.
 *
 * Reached from the imports dashboard rather than the sidebar. Registering a
 * source is setup done on the way to an import, not somewhere an operator sits
 * down to visit.
 */

/** A null `parserKey` is the CSV reader; say so rather than showing a blank. */
function extractorLabel(source: ImportSource): string {
	const key = source.parserKey ?? "csv";
	return EXTRACTOR_OPTIONS.find((one) => one.value === key)?.label ?? key;
}

function formatDate(value: string | null): string {
	if (!value) return "Never";
	return new Date(value).toLocaleDateString();
}

export function CatalogSourcesPage() {
	const queryClient = useQueryClient();
	const [dialog, setDialog] = useState<SourceDialogMode | null>(null);

	/*
	 * `?due=1` is the dashboard's "Sources due for a refresh" in full, in the
	 * work queue's order. Kept in the URL so that link lands here filtered.
	 */
	const [searchParams, setSearchParams] = useSearchParams();
	const dueOnly = searchParams.get("due") === "1";
	const sources = useQuery({
		// The SAME key the imports dashboard reads its picker from, so a source
		// registered here is in that picker without a second fetch or a second
		// invalidation to remember. The due list sits under it, so the same
		// invalidation reaches both.
		queryKey: dueOnly
			? ["catalog-import-sources", "due"]
			: ["catalog-import-sources"],
		queryFn: dueOnly ? listDueImportSources : listImportSources,
	});

	const rows = sources.data ?? [];

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div className="grid gap-1.5">
						<CardTitle>Import sources</CardTitle>
						<CardDescription>
							Where price lists come from. Every import is staged against one of
							these, and its brand decides which SKUs the rows key onto.{" "}
							<Link className="underline" to="/catalog/imports">
								Back to imports
							</Link>
						</CardDescription>
						<label className="flex items-center gap-2 text-sm">
							<input
								type="checkbox"
								checked={dueOnly}
								onChange={(event) =>
									setSearchParams(event.target.checked ? { due: "1" } : {})
								}
							/>
							Only sources due for a refresh
						</label>
					</div>
					<Button onClick={() => setDialog({ kind: "create" })}>
						Register a source
					</Button>
				</CardHeader>
				<CardContent>
					{sources.isPending ? (
						<div className="grid gap-2">
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
						</div>
					) : sources.isError ? (
						<div className="grid gap-3 rounded-2xl border border-dashed p-6 text-center">
							<p className="text-sm text-muted-foreground">
								Could not load sources.
							</p>
							<div>
								<Button
									variant="outline"
									size="sm"
									onClick={() => sources.refetch()}
								>
									Try again
								</Button>
							</div>
						</div>
					) : rows.length === 0 ? (
						dueOnly ? (
							<EmptyState
								title="No sources are due for a refresh"
								hint="Clear the filter to see every source."
							/>
						) : (
							<EmptyState
								title="No sources yet"
								hint="Register one to start importing. A source names the brand its rows belong to and how its documents are read."
							/>
						)
					) : (
						<DataTable
							rows={rows}
							rowKey={(source) => source.id}
							isRefreshing={sources.isFetching && !sources.isPending}
							columns={[
								{
									header: "Source",
									cell: (source) => (
										<div className="grid">
											<span className="font-medium">{source.name}</span>
											<span className="text-xs text-muted-foreground">
												{source.brandName ?? "(no brand)"}
											</span>
										</div>
									),
								},
								{
									header: "Read as",
									cell: (source) => extractorLabel(source),
								},
								{
									header: "Basis",
									cell: (source) => source.defaultBasis,
								},
								{
									header: "Every",
									cell: (source) => `${source.refreshCadenceDays} days`,
								},
								{
									header: "Last harvested",
									cell: (source) => formatDate(source.lastHarvestedAt),
								},
								{
									header: "",
									cell: (source) => (
										<Button
											variant="outline"
											size="sm"
											onClick={() => setDialog({ kind: "edit", source })}
											aria-label={`Edit ${source.name}`}
										>
											Edit
										</Button>
									),
								},
							]}
						/>
					)}
				</CardContent>
			</Card>

			{dialog ? (
				<SourceDialog
					mode={dialog}
					onClose={() => setDialog(null)}
					onSaved={() => {
						// One key, shared with the imports dashboard's picker -- so an
						// operator who has just registered a source can select it on the
						// screen they came from without a reload.
						queryClient.invalidateQueries({
							queryKey: ["catalog-import-sources"],
						});
					}}
				/>
			) : null}
		</div>
	);
}
